<template>
  <BaseRender
    class="industry-field-mapping"
    showDefaultValue
    :loading="loading"
    :fieldTypeInfo="fieldTypeInfo"
    :fieldInfo="fieldInfo"
    :mappingList="mappingList"
    :jobCustomField="jobCustomField"
    :showAddMapItemBtn.sync="showAddMapItemBtn"
    @save="handleSave"
    @addMapItem="handleAddMapItem"
    @jobCustomFieldChange="handleJobCustomFieldChange"
  >
    <template #defaultValue="{ refName }">
      <el-cascader
        v-model="defaultValue"
        class="industry-cascader"
        clearable
        filterable
        separator="-->"
        :ref="refName"
        :options="fieldTypeOptions"
        :props="{ value: 'id', label: 'i18nName' }"
        :placeholder="$t(`${i18nPrefix}.placeholder.defaultValue`)"
        @focus="clearError(refName)"
      ></el-cascader>
    </template>
    <template v-if="jobCustomField.field" #selfFieldRender="{ data }">
      <span class="job-custom-field-name">{{ data.jobCustomFieldOption.i18nName }}</span>
    </template>
    <template v-else #selfFieldRender="{ data, refName }">
      <el-select
        v-model="data.industryId"
        class="job-industry-select"
        filterable
        clearable
        :ref="`${refName}_industry`"
        :filter-method="filterIndustry"
        @visible-change="onIndustryVisibleChange"
        @change="onIndustryChange(data, $event)"
        @focus="clearError(`${refName}_industry`)"
      >
        <el-option
          v-for="(industry, industryId) in industryIdMap"
          v-show="industry.show"
          :key="industryId"
          :label="industry.i18nName"
          :value="industry.id"
        ></el-option>
      </el-select>
      <el-select
        v-model="data.jobTypeId"
        class="job-type-select"
        filterable
        clearable
        :ref="`${refName}_type`"
        :disabled="!data.industryId"
        :filter-method="(inputValue) => filterJobType(data, inputValue)"
        @visible-change="onJobTypeVisibleChange(data, $event)"
        @focus="clearError(`${refName}_type`)"
      >
        <el-option
          v-for="(jobType, index) in (data.industryId ? industryIdMap[data.industryId].children : [])"
          v-show="jobType.show"
          :key="index"
          :label="jobType.i18nName"
          :value="jobType.id"
        ></el-option>
      </el-select>
    </template>
    <template #otherFieldRender="{ data, refName }">
      <el-cascader
        v-model="data.value"
        class="industry-cascader"
        clearable
        filterable
        separator=" --> "
        :ref="refName"
        :options="fieldTypeOptions"
        :props="{ value: 'id', label: 'i18nName' }"
        @focus="clearError(refName)"
      ></el-cascader>
    </template>
  </BaseRender>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import channelId from '../channelId'
import mixin from '../mixin'
import { addI18nProperty, isEn } from '@/i18n'

class OtherIndustry {
  constructor (category, type, name) {
    this.industryCategory = category
    this.industryType = type
    this.industryName = name
  }

  getValue (key) {
    const industryCategory = this.industryCategory[key]
    const industryType = this.industryType[key]
    const industryName = this.industryName[key]
    return [industryCategory, industryType, industryName]
  }

  setValue (key, val) {
    this.industryCategory[key] = val[0]
    this.industryType[key] = val[1]
    this.industryName[key] = val[2]
  }

  get default () {
    return this.getValue('default')
  }

  set default (val) {
    this.setValue('default', val)
  }

  get value () {
    return this.getValue('value')
  }

  set value (val) {
    this.setValue('value', val)
  }
}

function createEmptyOtherIndustry () {
  return new OtherIndustry({ value: null }, { value: null }, { value: null })
}

export default {
  name: `channel-${channelId.job51}-fieldType-industry`,
  mixins: [mixin],
  data () {
    return {
      mappingList: [],
      industryList: [],
      industryIdMap: {},
      selectedJobType: {},
      fieldTypeOptions: [],
      showAddMapItemBtn: true,
      jobCustomField: {
        show: true,
        multiple: true,
        multipleLimit: 3,
        fieldId: null,
        field: null,
        fieldList: []
      }
    }
  },
  computed: {
    industryCategoryField () {
      return this.fieldInfo.industry_category
    },
    industryTypeField () {
      return this.fieldInfo.industry_type
    },
    industryNameField () {
      return this.fieldInfo.industry_name
    },
    defaultValue: {
      get () {
        const categoryId = this.industryCategoryField.default
        if (categoryId) {
          const typeId = this.industryTypeField.default
          const nameId = this.industryNameField.default
          return [categoryId, typeId, nameId]
        }
        return []
      },
      set (arr) {
        this.industryCategoryField.default = arr[0]
        this.industryTypeField.default = arr[1]
        this.industryNameField.default = arr[2]
      }
    }
  },
  created () {
    this.refreshPageData()
  },
  methods: {
    ...mapActions('job', ['getJobIndustryListWithJobType']),
    ...mapActions('jobPosting', ['getPostingFieldOptions']),
    ...mapActions('jobCustomField', ['searchJobCustomField']),
    async getDataFromApi () {
      await Promise.all([
        this.getJobIndustryListWithJobType().then(
          resp => {
            if (resp) {
              this.addI18nPropertyDeep(resp)
              const industryList = resp
              const industryIdMap = {}
              _.forEach(industryList, item => {
                item.show = true
                industryIdMap[item.id] = item
                _.forEach(item.children, child => {
                  child._parent = item
                  child.show = true
                })
              })
              this.industryList = industryList
              this.industryIdMap = industryIdMap
            }
          }
        ),
        this.getPostingFieldOptions({ fieldTypeId: this.fieldTypeInfo.id, params: { need_children: true } }).then(
          resp => {
            if (resp) {
              this.addI18nPropertyDeep(resp)
              this.fieldTypeOptions = resp
            }
          }
        ),
        this.searchJobCustomField({ status: 1, type: 'SELECT,SINGLE', need_options: true }).then(
          resp => {
            if (resp) {
              _.forEach(resp, (item) => addI18nProperty(item, 'i18nName', 'cn_name', 'name'))
              this.jobCustomField.fieldList = resp
              const fieldId = this.jobCustomField.fieldId
              if (fieldId && fieldId.length > 0) {
                this.jobCustomField.field = _.filter(this.jobCustomField.fieldList, (field) => fieldId.includes(field.id))
              }
            }
          }
        )
      ])
    },
    getSelfIndustry (relId) {
      let industry = null
      let jobType = null
      _.forEach(this.industryList, industryItem => {
        jobType = _.find(industryItem.children, { rel_id: relId })
        if (jobType) {
          industry = industryItem
        }
        return !industry
      })
      return { industry, jobType }
    },
    formatDataMap (dataMap) {
      if (!isNaN(dataMap.key)) {
        dataMap.key = parseInt(dataMap.key) || null
      }
      dataMap.value = parseInt(dataMap.value) || null
    },
    setOtherField (name, dataMap, otherFieldMap, dataKey) {
      this.formatDataMap(dataMap)
      if (!dataKey) {
        dataKey = dataMap.key
      }
      let otherField = null
      if (otherFieldMap.hasOwnProperty(dataKey)) {
        otherField = otherFieldMap[dataKey]
      } else {
        otherFieldMap[dataKey] = otherField = new OtherIndustry()
      }
      otherField[name] = dataMap
    },
    async refreshPageData (noNeedApiData) {
      this.loading = true
      try {
        const categoryField = this.industryCategoryField
        const typeField = this.industryTypeField
        const nameField = this.industryNameField
        if (categoryField.default) {
          categoryField.default = parseInt(categoryField.default)
          typeField.default = parseInt(typeField.default)
          nameField.default = parseInt(nameField.default)
        }
        let isJobCustomField = false
        if (categoryField.data_source === 'CUSTOM_FIELD') {
          const fieldId = categoryField.data_key_path
          this.jobCustomField.fieldId = _.map(fieldId.split(','), id => parseInt(id))
          this.showAddMapItemBtn = false
          isJobCustomField = true
        }
        if (!noNeedApiData) {
          await this.getDataFromApi()
        }
        const mappingList = []
        const industryCategoryDataMapList = categoryField.data_map_list
        const industryTypeDataMapList = typeField.data_map_list
        const industryNameDataMapList = nameField.data_map_list

        const otherFieldMap = {}
        const typeMap = {}
        const nameMap = {}
        _.forEach(industryCategoryDataMapList, (categoryDataMap, index) => {
          const typeDataMap = industryTypeDataMapList[index]
          const nameDataMap = industryNameDataMapList[index]
          this.formatDataMap(categoryDataMap)
          this.formatDataMap(typeDataMap)
          this.formatDataMap(nameDataMap)

          if (isJobCustomField) {
            this.setOtherField('industryCategory', categoryDataMap, otherFieldMap)
            this.setOtherField('industryType', typeDataMap, otherFieldMap)
            this.setOtherField('industryName', nameDataMap, otherFieldMap)
          } else {
            const otherField = new OtherIndustry(categoryDataMap, null, null)
            otherFieldMap[categoryDataMap.key] = otherField

            if (typeMap[categoryDataMap.key]) {
              otherField.industryType = typeMap[categoryDataMap.key]
            } else {
              if (categoryDataMap.key === typeDataMap.key) {
                otherField.industryType = typeDataMap
              } else {
                typeMap[typeDataMap.key] = typeDataMap
              }
            }

            if (nameMap[nameDataMap.key]) {
              otherField.industryName = nameMap[nameDataMap.key]
            } else {
              if (categoryDataMap.key === nameDataMap.key) {
                otherField.industryName = nameDataMap
              } else {
                nameMap[nameDataMap.key] = nameDataMap
              }
            }
          }
        })

        if (isJobCustomField) {
          const fieldId = this.jobCustomField.fieldId
          const fieldOptionsMap = {}
          _.forEach(this.jobCustomField.fieldList, (field) => {
            if (!fieldId.includes(field.id)) {
              return
            }
            fieldOptionsMap[field.id] = field.options
          })
          const fieldsOptionsList = this.exchangeFieldOption(_.map(fieldId, (id) => fieldOptionsMap[id]))
          _.forEach(fieldsOptionsList, (item) => {
            const optionVId = _.map(item, 'id').join(',')
            let otherField = null
            if (otherFieldMap.hasOwnProperty(optionVId)) {
              otherField = otherFieldMap[optionVId]
            } else {
              otherField = createEmptyOtherIndustry()
            }
            mappingList.push({
              id: mappingList.length + 1,
              self: {
                jobCustomFieldOption: {
                  id: optionVId,
                  get i18nName () {
                    return isEn() ? _.map(item, 'name').join(' / ') : _.map(item, 'cn_name').join(' / ')
                  }
                }
              },
              other: otherField
            })
          })
        } else {
          const vm = this
          _.forEach(otherFieldMap, (otherField) => {
            const selfField = {
              other: otherField,
              get relId () {
                return this.other.industryCategory.key
              },
              get industryId () {
                const category = this.other.industryCategory
                if (!category.hasOwnProperty('_industryId')) {
                  const { industry } = vm.getSelfIndustry(category.key)
                  category._industryId = industry.id
                }
                return category._industryId
              },
              set industryId (id) {
                this.other.industryCategory._industryId = id
                this.other.industryType._industryId = id
              },
              get jobTypeId () {
                const name = this.other.industryName
                if (!name.hasOwnProperty('_jobTypeId')) {
                  const { jobType } = vm.getSelfIndustry(name.key)
                  name._jobTypeId = jobType.id
                }
                return name._jobTypeId
              },
              set jobTypeId (id) {
                vm.selectedJobType[`${this.industryId}_${this.jobTypeId}`] = 0
                if (id) {
                  vm.selectedJobType[`${this.industryId}_${id}`] = 1
                }
                this.other.industryName._jobTypeId = id
                let relId = null
                if (id) {
                  const industry = vm.industryIdMap[this.industryId]
                  const jobType = _.find(industry.children, { id })
                  relId = jobType.rel_id
                }
                this.other.industryCategory.key = relId
                this.other.industryType.key = relId
                this.other.industryName.key = relId
              }
            }
            this.selectedJobType[`${selfField.industryId}_${selfField.jobTypeId}`] = 1
            this.onJobTypeVisibleChange(selfField, false)
            mappingList.push({
              id: mappingList.length + 1,
              self: selfField,
              other: otherField
            })
          })
        }
        this.mappingList = mappingList
      } finally {
        this.loading = false
      }
    },
    handleAddMapItem () {
      const vm = this
      this.mappingList.push({
        id: this.mappingList.length + 1,
        self: {
          industryId: null,
          _jobTypeId: null,
          relId: null,
          get jobTypeId () {
            return this._jobTypeId
          },
          set jobTypeId (id) {
            vm.selectedJobType[`${this.industryId}_${this.jobTypeId}`] = 0
            if (id) {
              vm.selectedJobType[`${this.industryId}_${id}`] = 1
            }
            this._jobTypeId = id
            let relId = null
            if (id) {
              const industry = vm.industryIdMap[this.industryId]
              const jobType = _.find(industry.children, { id })
              relId = jobType.rel_id
            }
            this.relId = relId
          }
        },
        other: createEmptyOtherIndustry()
      })
    },
    filterIndustry (inputValue) {
      _.forEach(this.industryIdMap, item => {
        const searchText = `${item.name}|${item.cn_name}|${item.pinyin_name}`
        item.show = inputValue ? searchText.includes(inputValue) : true
      })
    },
    onIndustryVisibleChange (isOpen) {
      if (!isOpen) {
        _.forEach(this.industryIdMap, item => {
          item.show = true
        })
      }
    },
    onIndustryChange (rowData, industryId) {
      rowData.jobTypeId = null
    },
    filterJobType (rowData, inputValue) {
      const industryId = rowData.industryId
      const industry = this.industryIdMap[industryId]
      _.forEach(industry.children, item => {
        const searchText = `${item.name}|${item.cn_name}|${item.pinyin_name}`
        const selectKey = `${industryId}_${item.id}`
        if (item.id === rowData.jobTypeId) {
          item.show = true
        } else if (!inputValue) {
          item.show = true
        } else if (this.selectedJobType[selectKey]) {
          item.show = false
        } else {
          item.show = searchText.includes(inputValue)
        }
      })
    },
    onJobTypeVisibleChange (rowData, isOpen) {
      const industryId = rowData.industryId
      const industry = this.industryIdMap[industryId]
      if (!industryId || !industry) {
        return
      }
      if (!isOpen) {
        _.forEach(industry.children, item => {
          const selectKey = `${industryId}_${item.id}`
          if (this.selectedJobType[selectKey]) {
            item.show = false
          } else {
            item.show = true
          }
        })
      } else {
        const jobType = _.find(industry.children, { id: rowData.jobTypeId })
        if (jobType) {
          jobType.show = true
        }
      }
    },
    handleJobCustomFieldChange (fieldId) {
      if (!fieldId || fieldId.length < 1) {
        this.mappingList = []
        return
      }
      const mappingList = []
      const jobCustomField = this.jobCustomField
      let fieldList = jobCustomField.field
      if (!fieldList || fieldList.length < 1) {
        fieldList = []
        _.forEach(jobCustomField.fieldList, (field) => {
          if (!fieldId.includes(field.id)) {
            return
          }
          fieldList.push(field)
        })
      }
      const fieldsOptionsList = this.exchangeFieldOption(_.map(fieldList, 'options'))
      _.forEach(fieldsOptionsList, item => {
        mappingList.push({
          id: mappingList.length + 1,
          self: {
            jobCustomFieldOption: {
              id: _.map(item, 'id').join(','),
              get i18nName () {
                return isEn() ? _.map(item, 'name').join(' / ') : _.map(item, 'cn_name').join(' / ')
              }
            }
          },
          other: createEmptyOtherIndustry()
        })
      })
      this.mappingList = mappingList
    },
    handleSave () {
      const errorNames = []
      if (!this.defaultValue || this.defaultValue.length < 1) {
        errorNames.push('defaultValue')
      }
      const categoryField = this.industryCategoryField
      const typeField = this.industryTypeField
      const nameField = this.industryNameField
      const isJobCustomField = this.jobCustomField.fieldId && this.jobCustomField.fieldId.length > 0
      const dataMapList = []
      let hasOldData = false
      _.forEach(this.mappingList, item => {
        const otherField = item.other
        const selfField = item.self
        if (!selfField.jobTypeId && !selfField.industryId &&
          !otherField.industryCategory.value && !otherField.industryType.value && !otherField.industryName.value) {
          return
        }
        if (!isJobCustomField) {
          if (!selfField.jobTypeId) {
            errorNames.push(`self_${item.id}_type`)
          }
          if (!selfField.industryId) {
            errorNames.push(`self_${item.id}_industry`)
          }
          if (!otherField.industryCategory.value || !otherField.industryType.value || !otherField.industryName.value) {
            errorNames.push(`other_${item.id}`)
          }
        }
        if (otherField.industryCategory.id) {
          hasOldData = true
          return
        }
        let dataMapKey = null
        if (isJobCustomField) {
          dataMapKey = selfField.jobCustomFieldOption.id
        } else {
          dataMapKey = selfField.relId
        }
        dataMapList.push({
          key: dataMapKey,
          value: {
            category: otherField.industryCategory.value,
            type: otherField.industryType.value,
            name: otherField.industryName.value
          }
        })
      })
      if (errorNames.length > 0) {
        this.setError(errorNames)
        return
      }
      if (!hasOldData) {
        categoryField.data_map_list = []
        typeField.data_map_list = []
        nameField.data_map_list = []
      }
      if (isJobCustomField) {
        categoryField.data_source = 'CUSTOM_FIELD'
        categoryField.data_key_path = `${this.jobCustomField.fieldId}`
        typeField.data_source = 'CUSTOM_FIELD'
        typeField.data_key_path = `${this.jobCustomField.fieldId}`
        nameField.data_source = 'CUSTOM_FIELD'
        nameField.data_key_path = `${this.jobCustomField.fieldId}`
      } else {
        categoryField.data_source = 'INDUSTRY_JOB_TYPE_REL'
        categoryField.data_key_path = 'id'
        typeField.data_source = 'INDUSTRY_JOB_TYPE_REL'
        typeField.data_key_path = 'id'
        nameField.data_source = 'INDUSTRY_JOB_TYPE_REL'
        nameField.data_key_path = 'id'
      }
      _.forEach(dataMapList, item => {
        categoryField.data_map_list.push({
          key: item.key,
          value: item.value.category
        })
        typeField.data_map_list.push({
          key: item.key,
          value: item.value.type
        })
        nameField.data_map_list.push({
          key: item.key,
          value: item.value.name
        })
      })
      this.$emit('save')
    },
    addI18nPropertyDeep (data, childName = 'children') {
      _.forEach(data, item => {
        addI18nProperty(item, 'i18nName', 'cn_name', 'name')
        if (item[childName] && item[childName].length > 0) {
          this.addI18nPropertyDeep(item[childName])
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.industry-field-mapping {
  ::v-deep {
    .el-table {
      .el-table__body {
        .cell {
          padding-left: 5px;
          padding-right: 5px;
        }

        .self-field {
          .cell {
            display: flex;
          }

          .job-industry-select {
            width: 130px;
            margin-right: 5px;
          }

          .job-type-select {
            flex: 1;
          }

          .job-custom-field-name {
            flex: 1;
          }
        }

        .other-field {
          .industry-cascader {
            .el-input__inner {
              padding-left: 5px;
            }
          }
        }
      }
    }

    .industry-cascader {
      width: 100%;
    }
  }

}
</style>
